import { useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import {
  UserPlus, MagnifyingGlass, ArrowsCounterClockwise, CheckCircle, XCircle,
  FunnelSimple, IdentificationCard, Copy
} from "@phosphor-icons/react";

const STATUS_META = {
  New:        { tone: "bg-sky-50 text-sky-800 border-sky-200",         label: "New" },
  Reviewed:   { tone: "bg-indigo-50 text-indigo-800 border-indigo-200", label: "Reviewed" },
  Assigned:   { tone: "bg-amber-50 text-amber-900 border-amber-200",   label: "Assigned" },
  Converted:  { tone: "bg-emerald-50 text-emerald-800 border-emerald-200", label: "Converted" },
  Rejected:   { tone: "bg-red-50 text-red-800 border-red-200",         label: "Rejected" },
  Duplicate:  { tone: "bg-slate-100 text-slate-700 border-slate-300",  label: "Duplicate" },
};

const fmt = (dt) => {
  if (!dt) return "—";
  try { return new Date(dt).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch { return dt; }
};

export default function NewLeads() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [assignedFilter, setAssignedFilter] = useState("any");
  const [page, setPage] = useState(1);
  const pageSize = 25;
  const [busy, setBusy] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [employees, setEmployees] = useState([]);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const params = { page, page_size: pageSize };
      if (q) params.q = q;
      if (statusFilter) params.status = statusFilter;
      if (assignedFilter && assignedFilter !== "any") params.assigned = assignedFilter;
      const { data } = await api.get("/leads", { params });
      setRows(data.rows || []); setTotal(data.total || 0);
    } catch (ex) {
      toast.error(formatApiError(ex.response?.data?.detail));
    } finally { setBusy(false); }
  }, [q, statusFilter, assignedFilter, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/directory/employees", { params: { page_size: 500 } })
      .then((r) => setEmployees(r.data?.rows || []))
      .catch(() => setEmployees([]));
  }, []);

  const summary = useMemo(() => {
    const s = { New: 0, Reviewed: 0, Assigned: 0, Converted: 0, Rejected: 0, Duplicate: 0 };
    rows.forEach((r) => { if (s[r.status] !== undefined) s[r.status] += 1; });
    return s;
  }, [rows]);

  return (
    <div className="space-y-5" data-testid="new-leads-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 tracking-tight">New Leads</h1>
          <p className="text-sm text-slate-500 mt-0.5">Candidates who signed up themselves via the unified login page.</p>
        </div>
        <button onClick={load} disabled={busy} data-testid="leads-refresh"
                className="text-sm inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 hover:bg-slate-50">
          <ArrowsCounterClockwise size={14} /> Refresh
        </button>
      </div>

      {/* Status counters */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {Object.entries(STATUS_META).map(([k, m]) => (
          <button key={k}
                  onClick={() => { setStatusFilter(statusFilter === k ? "" : k); setPage(1); }}
                  data-testid={`leads-stat-${k}`}
                  className={`text-left border rounded-xl px-4 py-3 transition
                              ${statusFilter === k ? "border-slate-900" : "border-slate-200 hover:border-slate-300"}`}>
            <div className={`text-[10px] uppercase tracking-widest inline-block px-1.5 py-0.5 rounded ${m.tone} border`}>
              {m.label}
            </div>
            <div className="text-2xl font-semibold text-slate-900 mt-1">{summary[k] || 0}</div>
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[240px]">
          <MagnifyingGlass size={14} className="absolute left-3 top-2.5 text-slate-400" />
          <input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }}
                 placeholder="Search by name, email or mobile"
                 data-testid="leads-search"
                 className="w-full border border-slate-200 rounded-md pl-8 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500/40" />
        </div>
        <select value={assignedFilter} onChange={(e) => { setAssignedFilter(e.target.value); setPage(1); }}
                data-testid="leads-assigned-filter"
                className="border border-slate-200 rounded-md px-3 py-2 text-sm">
          <option value="any">All owners</option>
          <option value="unassigned">Unassigned</option>
          {employees.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
        </select>
        <div className="ml-auto text-xs text-slate-500">
          {total} lead{total === 1 ? "" : "s"}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-[0.1em]">
            <tr>
              <th className="text-left px-4 py-2.5">Candidate</th>
              <th className="text-left px-4 py-2.5">Contact</th>
              <th className="text-left px-4 py-2.5">Signup</th>
              <th className="text-left px-4 py-2.5">Source</th>
              <th className="text-left px-4 py-2.5">Status</th>
              <th className="text-left px-4 py-2.5">Assigned To</th>
              <th className="text-left px-4 py-2.5">Profile %</th>
              <th className="text-right px-4 py-2.5">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && !busy && (
              <tr><td colSpan={8} className="px-4 py-12 text-center text-slate-400" data-testid="leads-empty">
                No new leads match the current filters.
              </td></tr>
            )}
            {rows.map((r) => {
              const meta = STATUS_META[r.status] || STATUS_META.New;
              return (
                <tr key={r.id} data-testid={`lead-row-${r.id}`} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-900" data-testid={`lead-name-${r.id}`}>{r.full_name || "—"}</div>
                    <div className="text-[11px] text-slate-400">{r.id.slice(0, 8)}…</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-slate-800">{r.email}</div>
                    <div className="text-xs text-slate-500">{r.mobile || "—"}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{fmt(r.signup_at)}</td>
                  <td className="px-4 py-3 text-xs text-slate-500">{r.signup_source}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[11px] px-2 py-0.5 rounded-full border font-medium ${meta.tone}`}
                          data-testid={`lead-status-${r.id}`}>
                      {meta.label}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{r.assigned_employee_name || "—"}</td>
                  <td className="px-4 py-3 text-slate-600">{r.profile_completion_pct ?? 0}%</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button onClick={() => setSelectedLead(r)}
                            data-testid={`lead-review-${r.id}`}
                            className="text-xs px-3 py-1.5 rounded-md border border-slate-200 hover:bg-slate-100 text-slate-700">
                      Review
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {total > pageSize && (
        <div className="flex items-center justify-between text-xs text-slate-500">
          <span>Page {page} of {Math.ceil(total / pageSize)}</span>
          <div className="flex gap-2">
            <button disabled={page <= 1} onClick={() => setPage(page - 1)}
                    className="px-3 py-1.5 rounded border border-slate-200 disabled:opacity-50">Previous</button>
            <button disabled={page * pageSize >= total} onClick={() => setPage(page + 1)}
                    className="px-3 py-1.5 rounded border border-slate-200 disabled:opacity-50">Next</button>
          </div>
        </div>
      )}

      {selectedLead && (
        <LeadReviewModal lead={selectedLead} employees={employees}
                         onClose={() => setSelectedLead(null)}
                         onDone={() => { setSelectedLead(null); load(); }} />
      )}
    </div>
  );
}

/* -------- Lead review + actions modal -------- */
function LeadReviewModal({ lead, employees, onClose, onDone }) {
  const [tab, setTab] = useState("summary");
  const [busy, setBusy] = useState(false);
  const [assignee, setAssignee] = useState("");
  const [reason, setReason] = useState("");
  const [masterLeadId, setMasterLeadId] = useState("");

  const act = async (path, body) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/leads/${lead.id}/${path}`, body || {});
      toast.success(data?.status ? `Lead marked ${data.status}` : "Done");
      onDone();
    } catch (ex) {
      toast.error(formatApiError(ex.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-40 bg-slate-900/60 backdrop-blur-sm grid place-items-center p-4"
         data-testid="lead-review-modal">
      <div className="w-full max-w-2xl bg-white rounded-2xl shadow-xl">
        <div className="p-6 border-b border-slate-100 flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2">
              <IdentificationCard size={22} className="text-slate-500" />
              <h3 className="text-xl font-semibold text-slate-900">{lead.full_name}</h3>
            </div>
            <p className="text-xs text-slate-500 mt-1">
              {lead.email} · {lead.mobile || "no mobile"} · signed up {fmt(lead.signup_at)}
            </p>
          </div>
          <button onClick={onClose} data-testid="lead-review-close"
                  className="p-1 text-slate-400 hover:text-slate-700"><XCircle size={20} /></button>
        </div>

        <div className="p-6 space-y-4">
          <div className="flex gap-2 text-xs">
            {["summary", "assign", "convert", "reject", "duplicate"].map((t) => (
              <button key={t} onClick={() => setTab(t)}
                      data-testid={`lead-tab-${t}`}
                      className={`px-3 py-1.5 rounded-full font-medium
                                  ${tab === t ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
                {t[0].toUpperCase() + t.slice(1)}
              </button>
            ))}
          </div>

          {tab === "summary" && (
            <div className="space-y-3 text-sm">
              <Info label="Status" value={lead.status} />
              <Info label="Source" value={lead.signup_source} />
              <Info label="Owner" value={lead.assigned_employee_name || "Unassigned"} />
              <Info label="Profile completion" value={`${lead.profile_completion_pct ?? 0}%`} />
              <Info label="Reason" value={lead.rejected_reason || lead.duplicate_reason || "—"} />
              <div>
                <div className="text-[11px] uppercase tracking-widest text-slate-500 font-medium mb-1">Activity</div>
                <ol className="border-l border-slate-200 pl-4 space-y-1.5 text-xs">
                  {(lead.events || []).map((e, i) => (
                    <li key={`ev-${e.at || ""}-${e.type || ""}-${i}`} className="text-slate-600">
                      <span className="font-medium text-slate-800">{e.type}</span> · {fmt(e.at)} — {e.message}
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          )}

          {tab === "assign" && (
            <div className="space-y-3 text-sm">
              <select value={assignee} onChange={(e) => setAssignee(e.target.value)}
                      data-testid="lead-assign-select"
                      className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm">
                <option value="">Select an employee</option>
                {employees.map((e) => <option key={e.id} value={e.id}>{e.name} · {e.designation || e.role}</option>)}
              </select>
              <button onClick={() => act("assign", { employee_id: assignee })}
                      disabled={busy || !assignee}
                      data-testid="lead-assign-submit"
                      className={`${busy ? "opacity-60" : ""} inline-flex items-center gap-1.5 rounded-lg bg-sky-600 hover:bg-sky-700 text-white text-sm font-medium py-2 px-4 disabled:opacity-50`}>
                <UserPlus size={14} /> Assign owner
              </button>
            </div>
          )}

          {tab === "convert" && (
            <div className="space-y-3 text-sm">
              <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3}
                        data-testid="lead-convert-reason"
                        placeholder="Optional notes about the conversion"
                        className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" />
              <button onClick={() => act("convert", { reason })}
                      disabled={busy}
                      data-testid="lead-convert-submit"
                      className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium py-2 px-4 disabled:opacity-50">
                <CheckCircle size={14} /> Convert to Candidate
              </button>
            </div>
          )}

          {tab === "reject" && (
            <div className="space-y-3 text-sm">
              <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} required
                        data-testid="lead-reject-reason"
                        placeholder="Reason (required)"
                        className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" />
              <button onClick={() => act("reject", { reason })}
                      disabled={busy || !reason.trim()}
                      data-testid="lead-reject-submit"
                      className="inline-flex items-center gap-1.5 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-medium py-2 px-4 disabled:opacity-50">
                <XCircle size={14} /> Reject
              </button>
            </div>
          )}

          {tab === "duplicate" && (
            <div className="space-y-3 text-sm">
              <input value={masterLeadId} onChange={(e) => setMasterLeadId(e.target.value)}
                     data-testid="lead-duplicate-master"
                     placeholder="Master lead ID (the original)"
                     className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" />
              <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3}
                        data-testid="lead-duplicate-reason"
                        placeholder="Optional notes"
                        className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" />
              <button onClick={() => act("mark-duplicate", { master_lead_id: masterLeadId, reason })}
                      disabled={busy || !masterLeadId}
                      data-testid="lead-duplicate-submit"
                      className="inline-flex items-center gap-1.5 rounded-lg bg-slate-700 hover:bg-slate-800 text-white text-sm font-medium py-2 px-4 disabled:opacity-50">
                <Copy size={14} /> Mark as Duplicate
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div className="flex items-baseline justify-between text-sm">
      <span className="text-[11px] uppercase tracking-widest text-slate-500 font-medium">{label}</span>
      <span className="text-slate-800">{value ?? "—"}</span>
    </div>
  );
}
